import { useState } from "react";
import { Send } from "lucide-react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Skeleton,
  StatusPill,
  toast,
} from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { BackButton } from "../../workspace/index.ts";
import { StorePageColumn } from "../store-page-column.tsx";
import { trpc } from "../../../lib/trpc.ts";
import { storeErrorMessage } from "../error-message.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { withGstPaise } from "../price-math.ts";
import { ComingSoonButton } from "./coming-soon-button.tsx";
import {
  QUOTE_PHASE_TONE,
  exactMoney,
  expiryLabel,
  quoteMoney,
  shortDate,
} from "./quote-format.ts";
import { shortDay, deliveryWindow } from "../date-window.ts";
import { PrebookPill } from "../prebook-pill.tsx";
import { BookingCard } from "../booking-card.tsx";

/**
 * Store admin — Quote detail (Commerce, M4).
 * @see prototype/store/admin/all.html#quotes-detail
 */
export function QuoteDetailPage({ quoteId }: { quoteId: string }) {
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const q = trpc.storeQuotes.get.useQuery({ quoteId });
  const canSend = usePermission("store.quotes.send");
  // Converting creates an ORDER, so it is the order right that gates it, not the
  // quote one — the API agrees (spec 16 R8).
  const canConvert = usePermission("store.orders.manage");
  const [convertOpen, setConvertOpen] = useState(false);

  const convert = trpc.storeQuotes.convert.useMutation({
    onSuccess: async ({ orderId }) => {
      // Both books move: the quote leaves `sent` and an order appears.
      await Promise.all([
        utils.storeQuotes.get.invalidate({ quoteId }),
        utils.storeQuotes.list.invalidate(),
        utils.storeQuotes.counts.invalidate(),
        utils.storeOrders.list.invalidate(),
        utils.storeOrders.counts.invalidate(),
      ]);
      toast.success("Order created from quote");
      setConvertOpen(false);
      // Invalidate → toast → navigate, in that order: navigating first lands on
      // an orders list rendered from a cache that predates this order.
      await navigate({ to: "/store/admin/orders/$orderId", params: { orderId } });
    },
    onError: (e) =>
      toast.error("Couldn't convert the quote", { description: storeErrorMessage(e) }),
  });

  const transition = trpc.storeQuotes.transition.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.storeQuotes.get.invalidate({ quoteId }),
        utils.storeQuotes.list.invalidate(),
        utils.storeQuotes.counts.invalidate(),
      ]);
      toast.success("Quote marked as sent");
    },
    onError: (e) => toast.error("Couldn't update the quote", { description: storeErrorMessage(e) }),
  });

  const quote = q.data;

  if (!quote) {
    return (
      <StoreAdminLayout
        breadcrumbs={[
          { label: "Store", to: "/store/admin" },
          { label: "Quotes", to: "/store/admin/quotes" },
          { label: "…" },
        ]}
      >
        <StorePageColumn>
          <BackButton fallbackTo="/store/admin/quotes" label="Back to quotes" />
          <div className="mt-4">
            {q.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <p className="text-sm text-muted">Quote not found.</p>
            )}
          </div>
        </StorePageColumn>
      </StoreAdminLayout>
    );
  }

  // Never label by status: a sent quote that somehow has no number must not
  // read "Draft quote".
  const title = quote.docNumber || "Unnumbered quote";
  const pillText = expiryLabel(quote.phase, quote.expiresOn, new Date());

  return (
    <StoreAdminLayout
      breadcrumbs={[
        { label: "Store", to: "/store/admin" },
        { label: "Quotes", to: "/store/admin/quotes" },
        { label: title },
      ]}
    >
      {/* A lines table beside the 320px rail — `grid`, not the form default. */}
      <StorePageColumn variant="grid">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line pb-5">
          <div className="flex items-start gap-4">
            <BackButton fallbackTo="/store/admin/quotes" label="Back to quotes" className="mt-1" />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-mono text-2xl font-bold tracking-tight text-ink leading-tight">
                  {title}
                </h1>
                <StatusPill tone={QUOTE_PHASE_TONE[quote.phase]} size="sm" dot>
                  {pillText}
                </StatusPill>
                <PrebookPill kind={quote.kind} />
              </div>
              <p className="mt-1 text-sm text-muted">
                {quote.customerName} · {quote.sentAt ? "Sent" : "Raised"}{" "}
                {/* Appending `T00:00:00Z` used to be the fix here; it isn't one —
                    it makes the *instant* explicit but the formatter still renders
                    in the viewer's zone, so a browser west of UTC still showed the
                    previous day. `shortDay` pins the formatter instead. */}
                {shortDate(quote.sentAt ?? quote.createdAt)} · Expires{" "}
                {quote.expiresOn ? shortDay(quote.expiresOn) : "never"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Drawn because the deck draws them, inert because each needs a
                module that doesn't exist yet. Dimming with a reason beats
                either hiding them or shipping a button that lies. */}
            <ComingSoonButton label="Download PDF" reason="PDF rendering" />
            {quote.sentAt ? <ComingSoonButton label="Email again" reason="Email sending" /> : null}
            {/* Live as of spec 16 (spec 23 R37/AC18 amended). Only offered on a
                `sent` quote: R6 refuses draft → converted, because that would
                mint an order against a price no buyer has seen. */}
            {canConvert && quote.status === "sent" ? (
              <Button
                intent="secondary"
                size="md"
                disabled={convert.isPending}
                onClick={() => setConvertOpen(true)}
              >
                Convert to order
              </Button>
            ) : null}
            {/* Without this a converted quote is a dead end — the order it
                produced has no route from here, and `convertedOrderId` has been
                on the wire unused since 0134. Guarded on non-null because the
                seed's two converted quotes never had it set. */}
            {quote.status === "converted" && quote.convertedOrderId ? (
              <Button intent="secondary" size="md" asChild>
                <Link
                  to="/store/admin/orders/$orderId"
                  params={{ orderId: quote.convertedOrderId }}
                >
                  View order
                </Link>
              </Button>
            ) : null}
            {canSend && quote.status === "draft" ? (
              <Button
                intent="primary"
                size="md"
                disabled={transition.isPending}
                onClick={() => transition.mutate({ quoteId, status: "sent" })}
              >
                <Send size={14} aria-hidden />
                Send to buyer
              </Button>
            ) : null}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold text-ink">Lines</h2>
            </CardHeader>
            <CardBody>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-left text-sm">
                  <thead className="border-b border-line text-xs uppercase tracking-wide text-muted">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Product</th>
                      <th className="px-3 py-2 text-left font-medium">SKU</th>
                      <th className="px-3 py-2 text-right font-medium">Qty</th>
                      <th className="px-3 py-2 text-right font-medium">Price / pack</th>
                      <th className="px-3 py-2 text-right font-medium">
                        <span className="whitespace-nowrap">Line value</span>{" "}
                        <span className="whitespace-nowrap">(incl. GST)</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.lines.map((l) => (
                      <tr key={l.id} className="border-b border-line">
                        <td className="px-3 py-2 text-ink">
                          {l.name}
                          {l.packName ? (
                            <div className="text-xs text-muted">{l.packName}</div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-muted">{l.sku ?? "—"}</td>
                        {/* Pack-denominated: qty is packs, pieces = qty × pack.pieces. */}
                        <td className="px-3 py-2 text-right text-ink">
                          {l.packPieces !== null ? (
                            <>
                              <span className="font-mono tabular-nums">{l.qty}</span>{" "}
                              <span className="text-xs text-muted">packs</span>
                              <div className="text-xs text-muted tabular-nums">
                                {l.qty * l.packPieces} pcs
                              </div>
                            </>
                          ) : (
                            <span className="font-mono tabular-nums">{l.qty}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-ink-2">
                          {exactMoney(l.capturedPricePaise)}
                        </td>
                        {/* Line value incl. GST — spec 13, AC11. */}
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-ink">
                          {exactMoney(withGstPaise(BigInt(l.lineTotalPaise), l.taxBps).toString())}
                        </td>
                      </tr>
                    ))}
                    {/* Totals live inside the lines table, as the deck draws it.
                        The deck shows only a combined "Total (incl. GST)" here;
                        the split is kept because a buyer-facing document that
                        hides its tax line is not much use to an accountant. */}
                    <tr className="font-semibold">
                      <td className="px-3 py-2 text-right text-ink" colSpan={4}>
                        Subtotal
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-ink">
                        {exactMoney(quote.subtotalPaise)}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-right text-muted" colSpan={4}>
                        GST
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-ink-2">
                        {exactMoney(quote.taxPaise)}
                      </td>
                    </tr>
                    <tr className="text-base font-semibold">
                      <td className="px-3 py-2 text-right text-ink" colSpan={4}>
                        Total (incl. GST)
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-ink">
                        {exactMoney(quote.totalPaise)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>

          <div className="grid gap-5 lg:content-start">
            {/* `overdue` is false by design: a quote promises nothing until it
                becomes an order, so a window in the past isn't late here. */}
            <BookingCard
              kind={quote.kind}
              deliveryStartsOn={quote.deliveryStartsOn}
              deliveryEndsOn={quote.deliveryEndsOn}
              sourceCollectionId={quote.sourceCollectionId}
              sourceCollectionName={quote.sourceCollectionName}
              sourceCollectionStatus={quote.sourceCollectionStatus}
            />
            <Card>
              <CardHeader>
                <h2 className="text-sm font-semibold text-ink">Buyer</h2>
              </CardHeader>
              <CardBody>
                <div className="font-medium text-ink">{quote.customerName}</div>
                {quote.customerGstin ? (
                  <div className="mt-0.5 font-mono text-xs text-muted">{quote.customerGstin}</div>
                ) : null}
                {quote.coverMessage ? (
                  <p className="mt-3 border-t border-line pt-3 text-xs text-ink-2">
                    {quote.coverMessage}
                  </p>
                ) : null}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h2 className="text-sm font-semibold text-ink">Activity</h2>
              </CardHeader>
              <CardBody>
                <ol className="grid gap-3 text-sm">
                  <li className="flex items-start justify-between gap-3">
                    <StatusPill tone="muted" size="sm" dot>
                      Raised
                    </StatusPill>
                    <span className="font-mono text-xs tabular-nums text-muted">
                      {shortDate(quote.createdAt)}
                    </span>
                  </li>
                  {quote.sentAt ? (
                    <li className="flex items-start justify-between gap-3">
                      <StatusPill tone="sky" size="sm" dot>
                        Sent
                      </StatusPill>
                      <span className="font-mono text-xs tabular-nums text-muted">
                        {shortDate(quote.sentAt)}
                      </span>
                    </li>
                  ) : null}
                  {quote.status === "converted" ? (
                    <li className="flex items-start justify-between gap-3">
                      <StatusPill tone="ok" size="sm" dot>
                        Converted
                      </StatusPill>
                      <span className="font-mono text-xs tabular-nums text-muted">—</span>
                    </li>
                  ) : null}
                </ol>
                {/* The deck also draws Opened / Downloaded / a scheduled
                    reminder. Every one of those needs email-open and PDF
                    download tracking, so they are absent rather than faked —
                    an invented timeline is easy to mistake for real history. */}
                <p className="mt-3 border-t border-line pt-3 text-xs text-muted">
                  Opens, downloads and reminders appear here once quotes are emailed.
                </p>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h2 className="text-sm font-semibold text-ink">Value</h2>
              </CardHeader>
              <CardBody>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted">Quote total</span>
                  <span className="font-mono tabular-nums text-ink">
                    {quoteMoney(quote.totalPaise)}
                  </span>
                </div>
                <p className="mt-3 border-t border-line pt-3 text-xs text-muted">
                  {quote.phase === "expired"
                    ? "This quote has lapsed. Raise a new one to re-quote at current prices."
                    : "Prices were captured when the quote was raised and won't move with the catalogue."}
                </p>
              </CardBody>
            </Card>
          </div>
        </div>

        <ConfirmDialog
          open={convertOpen}
          onOpenChange={setConvertOpen}
          title="Convert to order?"
          description="Creates a draft order from this quote at the prices captured here. The quote becomes Converted and can't be sent again."
          // Design 18 line 99. Small print rather than part of the description so
          // it doesn't compete with what the action does.
          footnote={
            quote.kind === "prebook" && quote.sourceCollectionName
              ? `Books ${quote.sourceCollectionName} · delivery ${deliveryWindow(
                  quote.deliveryStartsOn,
                  quote.deliveryEndsOn,
                )}.`
              : undefined
          }
          confirmLabel="Convert"
          pending={convert.isPending}
          onConfirm={async () => {
            await convert.mutateAsync({ quoteId });
          }}
        />
      </StorePageColumn>
    </StoreAdminLayout>
  );
}
